import { useState } from 'react';
import { useToast } from './Toast';
import { supabase } from '../supabaseClient';
import { invokeFunction } from '../supabaseFetch';

const SECTION_CONFIG = [
  { type: "overdue", label: "Overdue", color: "text-red-600", dotColor: "bg-red-500" },
  { type: "blocked", label: "Blocked", color: "text-red-500", dotColor: "bg-red-400" },
  { type: "pending", label: "Pending Approval", color: "text-amber-600", dotColor: "bg-amber-500" },
  { type: "change-order", label: "Change Orders", color: "text-blue-600", dotColor: "bg-blue-500" },
  { type: "insurance", label: "Insurance Expiring", color: "text-orange-600", dotColor: "bg-orange-500" },
  { type: "unassigned", label: "Unassigned", color: "text-slate-600", dotColor: "bg-slate-400" },
];

export default function DashboardActionItems({ items, onNavigate, onApproveChangeOrder, onUpdateTask, session }) {
  const { showToast } = useToast();
  const [groupBy, setGroupBy] = useState(() => sessionStorage.getItem('baulit_actionItemsGroup') || "type");
  const [expandedSections, setExpandedSections] = useState({});
  const [rejectingItem, setRejectingItem] = useState(null);
  const [rejectionNote, setRejectionNote] = useState("");

  function toggleGroupBy() {
    const next = groupBy === "type" ? "project" : "type";
    setGroupBy(next);
    sessionStorage.setItem('baulit_actionItemsGroup', next);
  }

  function toggleSection(key) {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  }

  async function handleApproveTask(item) {
    onUpdateTask(item.projectId, item.taskId, { status: "In Progress" });
    showToast("Task approved", "success");
    if (item.assignedUserId) {
      const { data: { session: freshSession } } = await supabase.auth.getSession();
      const token = freshSession?.access_token || session?.access_token;
      if (!token) return;
      invokeFunction('send-task-notification', token, {
        type: 'approved',
        project_id: item.projectId,
        task_id: item.taskId,
        task_name: item.taskName,
        actor_name: session?.user?.email || 'Project admin',
        assigned_user_id: item.assignedUserId,
      }).catch(err => console.error('Notification error:', err));
    }
  }

  async function handleRejectTask(item) {
    if (rejectingItem === item.taskId) {
      const reason = rejectionNote || 'Rejected';
      onUpdateTask(item.projectId, item.taskId, { status: "Blocked", rejectionNote });
      showToast("Task rejected", "success");
      setRejectingItem(null);
      setRejectionNote("");
      if (item.assignedUserId) {
        const { data: { session: freshSession } } = await supabase.auth.getSession();
        const token = freshSession?.access_token || session?.access_token;
        if (!token) return;
        invokeFunction('send-task-notification', token, {
          type: 'rejected',
          project_id: item.projectId,
          task_id: item.taskId,
          task_name: item.taskName,
          actor_name: session?.user?.email || 'Project admin',
          assigned_user_id: item.assignedUserId,
          rejection_reason: reason,
        }).catch(err => console.error('Notification error:', err));
      }
    } else {
      setRejectingItem(item.taskId);
      setRejectionNote("");
    }
  }

  function handleUnblock(item) {
    onUpdateTask(item.projectId, item.taskId, { status: "In Progress", rejectionNote: "" });
    showToast("Task unblocked", "success");
  }

  function handleApproveCO(item) {
    onApproveChangeOrder(item.projectId, item.coId, true);
    showToast("Change order approved", "success");
  }

  function handleRejectCO(item) {
    if (rejectingItem === item.coId) {
      onApproveChangeOrder(item.projectId, item.coId, false, rejectionNote);
      showToast("Change order rejected", "success");
      setRejectingItem(null);
      setRejectionNote("");
    } else {
      setRejectingItem(item.coId);
      setRejectionNote("");
    }
  }

  function handleItemClick(item) {
    if (item.type === "insurance") {
      onNavigate("vendor-insurance");
    } else if (item.projectId) {
      onNavigate("task-detail", item.projectId);
    }
  }

  if (items.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
        <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
          <svg className="w-6 h-6 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <path d="M20 6L9 17l-5-5" />
          </svg>
        </div>
        <p className="text-sm font-medium text-stone-700">All clear — nothing needs your attention right now.</p>
      </div>
    );
  }

  function renderActions(item) {
    const isRejecting = rejectingItem === (item.coId || item.taskId);

    if (item.type === "pending") {
      return (
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-1.5 mt-2 sm:mt-0 sm:ml-4 flex-shrink-0">
          {isRejecting ? (
            <div className="flex items-center gap-1.5">
              <input
                autoFocus
                type="text" value={rejectionNote}
                onChange={e => setRejectionNote(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") handleRejectTask(item); if (e.key === "Escape") { setRejectingItem(null); setRejectionNote(""); } }}
                placeholder="Rejection note..."
                className="text-xs border border-slate-300 rounded px-2 py-1 w-36 focus:outline-none focus:ring-1 focus:ring-amber-400"
              />
              <button onClick={() => handleRejectTask(item)} className="text-xs font-semibold text-red-600 hover:text-red-700">Confirm</button>
              <button onClick={() => { setRejectingItem(null); setRejectionNote(""); }} className="text-xs text-slate-400 hover:text-slate-600">Cancel</button>
            </div>
          ) : (
            <>
              <button onClick={(e) => { e.stopPropagation(); handleApproveTask(item); }}
                className="text-xs font-semibold px-2.5 py-1 rounded-full bg-green-100 text-green-700 hover:bg-green-200 transition-colors min-h-[28px]">
                Approve
              </button>
              <button onClick={(e) => { e.stopPropagation(); handleRejectTask(item); }}
                className="text-xs font-semibold px-2.5 py-1 rounded-full border border-red-200 text-red-600 hover:bg-red-50 transition-colors min-h-[28px]">
                Reject
              </button>
            </>
          )}
        </div>
      );
    }

    if (item.type === "change-order") {
      return (
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-1.5 mt-2 sm:mt-0 sm:ml-4 flex-shrink-0">
          {isRejecting ? (
            <div className="flex items-center gap-1.5">
              <input
                autoFocus
                type="text" value={rejectionNote}
                onChange={e => setRejectionNote(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") handleRejectCO(item); if (e.key === "Escape") { setRejectingItem(null); setRejectionNote(""); } }}
                placeholder="Rejection note..."
                className="text-xs border border-slate-300 rounded px-2 py-1 w-36 focus:outline-none focus:ring-1 focus:ring-amber-400"
              />
              <button onClick={() => handleRejectCO(item)} className="text-xs font-semibold text-red-600 hover:text-red-700">Confirm</button>
              <button onClick={() => { setRejectingItem(null); setRejectionNote(""); }} className="text-xs text-slate-400 hover:text-slate-600">Cancel</button>
            </div>
          ) : (
            <>
              <button onClick={(e) => { e.stopPropagation(); handleApproveCO(item); }}
                className="text-xs font-semibold px-2.5 py-1 rounded-full bg-green-100 text-green-700 hover:bg-green-200 transition-colors min-h-[28px]">
                Approve
              </button>
              <button onClick={(e) => { e.stopPropagation(); handleRejectCO(item); }}
                className="text-xs font-semibold px-2.5 py-1 rounded-full border border-red-200 text-red-600 hover:bg-red-50 transition-colors min-h-[28px]">
                Reject
              </button>
            </>
          )}
        </div>
      );
    }

    if (item.type === "blocked") {
      return (
        <div className="mt-2 sm:mt-0 sm:ml-4 flex-shrink-0">
          <button onClick={(e) => { e.stopPropagation(); handleUnblock(item); }}
            className="text-xs font-semibold px-2.5 py-1 rounded-full bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors min-h-[28px]">
            Unblock
          </button>
        </div>
      );
    }

    return null;
  }

  function renderItem(item) {
    const hasActions = item.type === "pending" || item.type === "change-order" || item.type === "blocked";
    const isClickable = !hasActions || item.type === "blocked";
    const name = item.taskName || item.coTitle || item.contactName || "";
    const sub = item.projectName || item.company || "";

    return (
      <div
        key={`${item.type}-${item.taskId || item.coId || item.contactName}-${item.projectId || ""}`}
        onClick={isClickable ? () => handleItemClick(item) : undefined}
        className={`flex flex-col sm:flex-row sm:items-center justify-between py-2.5 -mx-2 px-2 rounded-lg ${isClickable ? "cursor-pointer hover:bg-slate-50" : ""} transition-colors group`}
      >
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-stone-800 truncate">{name}</p>
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <span className="truncate">{sub}</span>
            {item.detail && <span className="text-slate-300">·</span>}
            {item.detail && <span className="truncate">{item.detail}</span>}
            {item.assignedTo && <span className="text-slate-300">·</span>}
            {item.assignedTo && <span className="truncate">{item.assignedTo}</span>}
          </div>
        </div>
        {renderActions(item)}
      </div>
    );
  }

  function renderSection(label, sectionItems, sectionKey, dotColor) {
    if (sectionItems.length === 0) return null;
    const isExpanded = expandedSections[sectionKey];
    const visible = isExpanded ? sectionItems : sectionItems.slice(0, 5);

    return (
      <div key={sectionKey} className="mb-4 last:mb-0">
        <div className="flex items-center gap-2 mb-1">
          <span className={`w-2 h-2 rounded-full ${dotColor}`} />
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
            {label} ({sectionItems.length})
          </h4>
        </div>
        <div className="divide-y divide-slate-100">
          {visible.map(renderItem)}
        </div>
        {sectionItems.length > 5 && !isExpanded && (
          <button onClick={() => toggleSection(sectionKey)}
            className="text-xs text-amber-600 font-medium mt-1 hover:text-amber-700">
            Show all {sectionItems.length} →
          </button>
        )}
        {sectionItems.length > 5 && isExpanded && (
          <button onClick={() => toggleSection(sectionKey)}
            className="text-xs text-slate-400 font-medium mt-1 hover:text-slate-600">
            Show less
          </button>
        )}
      </div>
    );
  }

  // Group by type
  if (groupBy === "type") {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-stone-800">
            Action Items ({items.length})
          </h3>
          <button onClick={toggleGroupBy}
            className="text-xs font-medium px-3 py-1 rounded-full border border-slate-200 text-slate-500 hover:border-slate-400 hover:text-slate-700 transition-colors">
            By Project
          </button>
        </div>
        {SECTION_CONFIG.map(sec => {
          const sectionItems = items.filter(i => i.type === sec.type);
          return renderSection(sec.label, sectionItems, sec.type, sec.dotColor);
        })}
      </div>
    );
  }

  // Group by project
  const projectMap = new Map();
  for (const item of items) {
    const key = item.projectId || "_other";
    const name = item.projectName || item.contactName || "Other";
    if (!projectMap.has(key)) projectMap.set(key, { name, items: [] });
    projectMap.get(key).items.push(item);
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-stone-800">
          Action Items ({items.length})
        </h3>
        <button onClick={toggleGroupBy}
          className="text-xs font-medium px-3 py-1 rounded-full border border-slate-200 text-slate-500 hover:border-slate-400 hover:text-slate-700 transition-colors">
          By Type
        </button>
      </div>
      {Array.from(projectMap.entries()).map(([key, group]) => (
        renderSection(group.name, group.items, key, "bg-brand-navy")
      ))}
    </div>
  );
}
